import { useToast } from '@chakra-ui/react'
import { useState } from 'react'
import { UseFormTrigger } from 'react-hook-form'
import type { PropertyFormInput, StepSchemas } from '../schemas/propertySchema'
import {
  areRequiredStepsValid,
  canNavigateToStep,
  getFormSteps,
  type FormStep,
} from '../utils/propertyFormSteps'

interface UsePropertyFormStepsOptions {
  trigger: UseFormTrigger<PropertyFormInput<boolean>>
  stepSchemas: StepSchemas
}

/**
 * Hook for managing form step navigation and validation
 */
export function usePropertyFormSteps({
  trigger,
  stepSchemas,
}: UsePropertyFormStepsOptions) {
  const toast = useToast()
  const [currentStep, setCurrentStep] = useState(0)
  const steps = getFormSteps(stepSchemas)

  /**
   * Validate current step
   */
  const validateStep = async (
    stepIndex: number,
    formData: PropertyFormInput<boolean>
  ): Promise<boolean> => {
    const step = steps[stepIndex]
    if (!step) return false

    const result = step.schema.safeParse(formData)
    if (!result.success) {
      // Get the field names from the schema to trigger validation only for them
      const fieldsToValidate = Object.keys(
        step.schema.shape
      ) as (keyof PropertyFormInput<boolean>)[]
      await trigger(fieldsToValidate)
    }
    return result.success
  }

  /**
   * Check if all required steps are valid
   */
  const checkRequiredStepsValid = (
    formData: PropertyFormInput<boolean>
  ): boolean => {
    return areRequiredStepsValid(formData, steps)
  }

  /**
   * Navigate to a specific step
   */
  const goToStep = async (
    stepIndex: number,
    formData: PropertyFormInput<boolean>
  ) => {
    if (stepIndex < 0 || stepIndex >= steps.length) return

    // Check if navigation is allowed
    if (!canNavigateToStep(stepIndex, currentStep, formData, steps)) {
      const currentStepData = steps[currentStep]
      if (currentStepData?.required) {
        toast({
          title: 'Preencha os campos obrigatórios',
          description: 'Complete os campos obrigatórios antes de continuar',
          status: 'warning',
          duration: 3000,
        })
        return
      }
    }

    // Validate current step before moving forward
    if (stepIndex > currentStep) {
      const isCurrentStepValid = await validateStep(currentStep, formData)
      if (!isCurrentStepValid && steps[currentStep].required) {
        toast({
          title: 'Preencha os campos obrigatórios',
          description: 'Complete os campos obrigatórios antes de continuar',
          status: 'warning',
          duration: 3000,
        })
        return
      }
    }

    setCurrentStep(stepIndex)
  }

  /**
   * Navigate to next step
   */
  const nextStep = (formData: PropertyFormInput<boolean>) => {
    goToStep(currentStep + 1, formData)
  }

  /**
   * Navigate to previous step
   */
  const prevStep = (formData: PropertyFormInput<boolean>) => {
    goToStep(currentStep - 1, formData)
  }

  /**
   * Check if we can navigate to next step
   */
  const canGoNext = (formData: PropertyFormInput<boolean>): boolean => {
    return canNavigateToStep(currentStep + 1, currentStep, formData, steps)
  }

  /**
   * Check if we can navigate to previous step
   */
  const canGoPrev = (): boolean => {
    return currentStep > 0
  }

  /**
   * Get current step data
   */
  const getCurrentStep = (): FormStep | undefined => {
    return steps[currentStep]
  }

  /**
   * Check if current step is the last step
   */
  const isLastStep = (): boolean => {
    return currentStep === steps.length - 1
  }

  /**
   * Check if current step is the first step
   */
  const isFirstStep = (): boolean => {
    return currentStep === 0
  }

  return {
    // State
    currentStep,
    steps,

    // Navigation
    goToStep,
    nextStep,
    prevStep,
    canGoNext,
    canGoPrev,

    // Validation
    validateStep,
    areRequiredStepsValid: checkRequiredStepsValid,

    // Utilities
    getCurrentStep,
    isLastStep,
    isFirstStep,
  }
}
